import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  FormControl,
  Grid,
  GridItem,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../../../Globalapi/Globalapi';

const TransferCertificate = ({ user_id }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalMode, setModalMode] = useState('generate');
  const [details, setDetails] = useState({
    tc_no: '',
    admission_no: '',
    pupil_name: '',
    guardian_name: '',
    nationality: '',
    religion_caste: '',
    date_of_first_admission: '',
    date_of_birth: '',
    class_last_studied: '',
    qualified_for_promotion: '',
    fees_paid: '',
    fee_concession: '',
    working_days: '',
    present_days: '',
    ncc_or_guide: '',
    general_conduct: '',
    application_date: '',
    issue_date: '',
    reason_for_leaving: '',
    tc_id: '', // Ensure tc_id is part of the state
  });
  const toast = useToast();

  useEffect(() => {
    if (user_id) {
      fetchStudentDetails();
    }
  }, [user_id, modalMode]);

  const fetchStudentDetails = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/student/view_student.php?user_id=${user_id}`);
      if (response.data.success) {
        const student = response.data.data;
        setDetails((prevDetails) => ({
          ...prevDetails,
          admission_no: student.admission_number,
          pupil_name: student.id,
          guardian_name: student.guardian_name,
          date_of_birth: student.date_of_birth,
        }));
      } else {
        console.error('Error fetching student details:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
    }
  };

  const handleGenerate = () => {
    setModalMode('generate');
    onOpen();
  };

  const handleEdit = () => {
    setModalMode('edit');
    onOpen();
  };

  const handleSubmit = async () => {
    const url = modalMode === 'generate'
      ? `${BASE_URL}/admin/certificates/create_tc.php`
      : `${BASE_URL}/admin/certificates/edit_tc.php`;

    const dataToSend = { ...details };
    try {
      await axios.post(url, dataToSend);
      toast({
        title: modalMode === 'generate' ? 'Certificate Generated' : 'Certificate Edited',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while processing your request.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const fetchCertificateDetails = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/admin/certificates/view_tc.php?certificate_id=${user_id}`);
        if (response.data.success) {
          const certificate = response.data.data[0];
          setDetails((prevDetails) => ({
            ...prevDetails,
            tc_id: certificate.tc_id, // Set the tc_id here
          }));
        } else {
          console.error('No certificate found for this student');
        }
      } catch (error) {
        console.error('Error fetching certificate details:', error);
      }
    };

    fetchCertificateDetails();
  }, [user_id]);

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
      <Text mb={2} fontWeight="bold">Transfer Certificate</Text>

      <Button colorScheme="blue" onClick={handleGenerate}>Generate</Button> &nbsp;
      <Button colorScheme="yellow" onClick={handleEdit}>Edit</Button>&nbsp;&nbsp;
      <Button as={Link} to={`/viewtransfer/${details.tc_id}`} colorScheme="green">
        View
      </Button>


      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalMode === 'generate' ? 'Generate Certificate' : 'Edit Certificate'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                {/* Left side - 9 fields */}
                <FormControl mb={4}>
                  <FormLabel>TC No</FormLabel>
                  <Input
                    placeholder="TC Number"
                    value={details.tc_no}
                    onChange={(e) => setDetails({ ...details, tc_no: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Admission No</FormLabel>
                  <Input
                    placeholder="Admission Number"
                    value={details.admission_no}
                    onChange={(e) => setDetails({ ...details, admission_no: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Pupil Name</FormLabel>
                  <Input
                    placeholder="Pupil Name"
                    value={details.pupil_name} // Display the name
                    onChange={(e) => setDetails({ ...details, pupil_name: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Guardian Name</FormLabel>
                  <Input
                    placeholder="Guardian Name"
                    value={details.guardian_name}
                    onChange={(e) => setDetails({ ...details, guardian_name: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Nationality</FormLabel>
                  <Input
                    placeholder="Nationality"
                    value={details.nationality}
                    onChange={(e) => setDetails({ ...details, nationality: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Religion/Caste</FormLabel>
                  <Input
                    placeholder="Religion/Caste"
                    value={details.religion_caste}
                    onChange={(e) => setDetails({ ...details, religion_caste: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Schedule Caste or Tribe</FormLabel>
                  <Input
                    placeholder="Schedule Caste or Tribe"
                    value={details.schedule_caste_or_tribe}
                    onChange={(e) => setDetails({ ...details, schedule_caste_or_tribe: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Date of First Admission</FormLabel>
                  <Input
                    type="date"
                    value={details.date_of_first_admission}
                    onChange={(e) => setDetails({ ...details, date_of_first_admission: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Date of Birth</FormLabel>
                  <Input
                    type="date"
                    value={details.date_of_birth}
                    onChange={(e) => setDetails({ ...details, date_of_birth: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Class Last Studied</FormLabel>
                  <Input
                    placeholder="Class Last Studied"
                    value={details.class_last_studied}
                    onChange={(e) => setDetails({ ...details, class_last_studied: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                {/* Right side - Remaining fields */}
                <FormControl mb={4}>
                  <FormLabel>Qualified for Promotion</FormLabel>
                  <Input
                    placeholder="Qualified for Promotion"
                    value={details.qualified_for_promotion}
                    onChange={(e) => setDetails({ ...details, qualified_for_promotion: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Fees Paid</FormLabel>
                  <Input
                    placeholder="Fees Paid"
                    value={details.fees_paid}
                    onChange={(e) => setDetails({ ...details, fees_paid: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Fee Concession</FormLabel>
                  <Input
                    placeholder="Fee Concession"
                    value={details.fee_concession}
                    onChange={(e) => setDetails({ ...details, fee_concession: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Working Days</FormLabel>
                  <Input
                    placeholder="Working Days"
                    value={details.working_days}
                    onChange={(e) => setDetails({ ...details, working_days: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Present Days</FormLabel>
                  <Input
                    placeholder="Present Days"
                    value={details.present_days}
                    onChange={(e) => setDetails({ ...details, present_days: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>NCC or Guide</FormLabel>
                  <Input
                    placeholder="NCC or Guide"
                    value={details.ncc_or_guide}
                    onChange={(e) => setDetails({ ...details, ncc_or_guide: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>General Conduct</FormLabel>
                  <Input
                    placeholder="General Conduct"
                    value={details.general_conduct}
                    onChange={(e) => setDetails({ ...details, general_conduct: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Application Date</FormLabel>
                  <Input
                    type="date"
                    value={details.application_date}
                    onChange={(e) => setDetails({ ...details, application_date: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Issue Date</FormLabel>
                  <Input
                    type="date"
                    value={details.issue_date}
                    onChange={(e) => setDetails({ ...details, issue_date: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Reason for Leaving</FormLabel>
                  <Input
                    placeholder="Reason for Leaving"
                    value={details.reason_for_leaving}
                    onChange={(e) => setDetails({ ...details, reason_for_leaving: e.target.value })}
                    isReadOnly={modalMode === 'view'}
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </ModalBody>

          <ModalFooter>
            {modalMode !== 'view' && (
              <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
                {modalMode === 'generate' ? 'Generate' : 'Edit'}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TransferCertificate;
